import { Link } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Swords, Users, Vote, Crown, Clock } from "lucide-react";

const MatchupCard = ({ debate, showUserBadge = false, userId = null }) => {
  const totalVotes = debate.red_votes + debate.blue_votes;
  const redPercent = totalVotes > 0 ? (debate.red_votes / totalVotes) * 100 : 50;
  const isWinner = (cornerId) => debate.status === "completed" && debate.winner_id === cornerId;
  
  const getUserBadge = () => {
    if (!showUserBadge || !userId) return null;
    if (debate.red_corner_id === userId) return "Red Corner";
    if (debate.blue_corner_id === userId) return "Blue Corner";
    return null;
  };

  return (
    <Link
      to={`/debate/${debate.debate_id}`}
      data-testid={`debate-card-${debate.debate_id}`}
      className="group relative overflow-hidden bg-card border border-white/10 hover:border-primary/50 transition-all duration-300 rounded-lg debate-card"
    >
      {/* Status Badge */}
      <div className="absolute top-3 right-3 z-10">
        <Badge 
          variant={debate.status === "live" ? "destructive" : debate.status === "open" ? "secondary" : "outline"}
          className="uppercase text-xs font-bold"
        >
          {debate.status === "live" && <span className="w-2 h-2 bg-white rounded-full mr-1.5 animate-pulse-live"></span>}
          {debate.status}
        </Badge>
      </div>

      {/* User Badge */}
      {getUserBadge() && (
        <div className="absolute top-3 left-3 z-10">
          <Badge variant="outline" className="text-xs">
            {getUserBadge()}
          </Badge>
        </div>
      )}

      {/* Matchup Header */}
      <div className="p-5 bg-gradient-to-r from-[#e11d48]/10 via-transparent to-[#3b82f6]/10">
        <div className="flex items-center justify-between">
          {/* Red Corner */}
          <div className="flex-1 text-center">
            <div className={`relative inline-block ${isWinner(debate.red_corner_id) ? 'stage-glow-gold' : ''}`}>
              {isWinner(debate.red_corner_id) && (
                <Crown className="absolute -top-4 left-1/2 -translate-x-1/2 w-5 h-5 text-accent animate-float" />
              )}
              <Avatar className="w-14 h-14 border-2 border-[#e11d48] mx-auto">
                <AvatarImage src={debate.red_corner_picture || "https://images.unsplash.com/photo-1712863132626-60bb701a6f4a?crop=entropy&cs=srgb&fm=jpg&q=85"} />
                <AvatarFallback className="bg-[#e11d48]">{debate.red_corner_username?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
            </div>
            <p className="text-xs font-medium mt-2 truncate max-w-[80px] mx-auto">
              {debate.red_corner_username || "TBD"}
            </p>
          </div>

          {/* VS Icon */}
          <div className="px-3">
            <div className="w-10 h-10 rounded-full bg-zinc-800 border border-white/10 flex items-center justify-center">
              <Swords className="w-5 h-5 text-muted-foreground" />
            </div>
          </div>

          {/* Blue Corner */}
          <div className="flex-1 text-center">
            <div className={`relative inline-block ${isWinner(debate.blue_corner_id) ? 'stage-glow-gold' : ''}`}>
              {isWinner(debate.blue_corner_id) && (
                <Crown className="absolute -top-4 left-1/2 -translate-x-1/2 w-5 h-5 text-accent animate-float" />
              )}
              <Avatar className="w-14 h-14 border-2 border-[#3b82f6] mx-auto">
                <AvatarImage src={debate.blue_corner_picture || "https://images.unsplash.com/photo-1648493696785-c0b27b0d93a5?crop=entropy&cs=srgb&fm=jpg&q=85"} />
                <AvatarFallback className="bg-[#3b82f6]">{debate.blue_corner_username?.[0]?.toUpperCase() || "?"}</AvatarFallback>
              </Avatar>
            </div>
            <p className="text-xs font-medium mt-2 truncate max-w-[80px] mx-auto">
              {debate.blue_corner_username || "Waiting..."}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 pt-4">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="outline" className="text-xs">{debate.category}</Badge>
          {debate.is_ai_generated && <Badge variant="outline" className="text-xs bg-purple-500/10 border-purple-500/30">🤖 AI</Badge>}
        </div>
        <h3 className="font-bold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {debate.title}
        </h3>
        
        {/* Positions - Show if available */}
        {(debate.red_position || debate.blue_position) ? (
          <div className="space-y-2 mb-4">
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 bg-red-500 rounded-full mt-1.5 shrink-0"></span>
              <p className="text-xs text-red-400">{debate.red_position || "Position TBD"}</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 shrink-0"></span>
              <p className="text-xs text-blue-400">{debate.blue_position || "Position TBD"}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
            {debate.description}
          </p>
        )}

        {/* Vote Bar (for live/completed) */}
        {(debate.status === "live" || debate.status === "completed") && totalVotes > 0 && (
          <div className="mb-4">
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden flex">
              <div 
                className="bg-[#e11d48] transition-all duration-500"
                style={{ width: `${redPercent}%` }}
              />
              <div 
                className="bg-[#3b82f6] transition-all duration-500"
                style={{ width: `${100 - redPercent}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>{debate.red_votes} votes</span>
              <span>{debate.blue_votes} votes</span>
            </div>
          </div>
        )}

        {/* Footer Stats */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Vote className="w-3 h-3" />
            {debate.total_votes} votes
          </span>
          {debate.status === "open" && (
            <span className="flex items-center gap-1 text-secondary">
              <Users className="w-3 h-3" />
              Needs opponent
            </span>
          )}
          {debate.status === "completed" && debate.winner_id && (
            <span className="flex items-center gap-1 text-accent">
              <Crown className="w-3 h-3" />
              Winner declared
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default MatchupCard;
